import { TableCell } from '@mui/material';
import styled from 'styled-components';
import { fadeInKeyframes, pulseAnimation } from './animations';
import { COLORS, SHADOWS, TIMINGS } from './constants';

export const HoverableStyledTableCell = styled(TableCell)`
  padding: 0.2rem 0.4rem !important;
  vertical-align: middle;
  position: relative;
  height: auto;
  min-height: 0;
  box-sizing: border-box;
  font-size: 0.7rem;
  line-height: 1.2;
  contain: layout style;

  &[data-editable] {
    cursor: text;
  }

  &[data-editing]:not([data-saving-status]) {
    background: ${COLORS.white} !important;
    box-shadow: ${SHADOWS.border(COLORS.primary)};
    padding: calc(0.2rem - 2px) calc(0.4rem - 2px) !important;
    min-width: 175px !important;
    & > * {
      margin: 0 !important;
      display: flex;
      align-items: center;
    }
  }

  &[data-editing]:not([data-saving-status])[data-column='containerType'] {
    min-width: 125px !important;
  }
  &[data-editing]:not([data-saving-status])[data-column='pickupDate'],
  &[data-editing]:not([data-saving-status])[data-column='loadingDeliveryDate'],
  &[data-editing]:not([data-saving-status])[data-column='deliveryDate'] {
    min-width: 150px !important;
    width: 150px !important;
    max-width: 150px !important;
  }
  &[data-editing]:not([data-saving-status])[data-column='comment'] {
    min-width: 400px !important;
  }
  &[data-editing]:not([data-saving-status])[data-column='referenceNumbers'] {
    min-width: 400px !important;
    width: 400px !important;
    max-width: 400px !important;
  }
  &[data-editing]:not([data-saving-status])[data-column='triangulation'] {
    min-width: 175px !important;
    width: 175px !important;
    max-width: 175px !important;
  }
  &[data-editing]:not([data-saving-status])[data-column='shippingLineHaulage'] {
    min-width: 0 !important;
    width: auto !important;
  }

  ${pulseAnimation}

  &[data-saving-status='loading'] {
    background: ${COLORS.white};
    padding: calc(0.2rem - 2px) calc(0.4rem - 2px) !important;
    contain: layout style;
    min-width: auto !important;
    width: auto !important;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      box-shadow: ${SHADOWS.border(COLORS.loading)};
      animation: pulse ${TIMINGS.animationPulse} ease-in-out infinite;
      will-change: opacity;
    }
  }

  &[data-saving-status='success'] {
    background: ${COLORS.white};
    box-shadow: ${SHADOWS.border(COLORS.success)};
    padding: calc(0.2rem - 2px) calc(0.4rem - 2px) !important;
    transition: box-shadow ${TIMINGS.transitionNormal} ease,
      padding ${TIMINGS.transitionNormal} ease;
    min-width: auto !important;
    width: auto !important;
  }

  &[data-saving-status='success-other'] {
    background: ${COLORS.white};
    box-shadow: ${SHADOWS.border(COLORS.loading)};
    padding: calc(0.2rem - 2px) calc(0.4rem - 2px) !important;
    transition: box-shadow ${TIMINGS.transitionNormal} ease,
      padding ${TIMINGS.transitionNormal} ease;
    min-width: auto !important;
    width: auto !important;
  }

  &[data-saving-status='error'] {
    background: ${COLORS.white};
    box-shadow: ${SHADOWS.border(COLORS.errorLight)};
    padding: calc(0.2rem - 2px) calc(0.4rem - 2px) !important;
    transition: box-shadow ${TIMINGS.transitionNormal} ease,
      padding ${TIMINGS.transitionNormal} ease;
    min-width: auto !important;
    width: auto !important;
  }

  &[data-saving-status='fading'],
  &[data-saving-status='fading-other'] {
    background: ${COLORS.white};
    box-shadow: inset 0 0 0 2px transparent;
    padding: 0.2rem 0.4rem !important;
    transition: box-shadow ${TIMINGS.transitionSlow} ease,
      padding ${TIMINGS.transitionSlow} ease;
  }

  &[data-validation-error][data-validation-status='active'] {
    background: ${COLORS.white};
    box-shadow: ${SHADOWS.border(COLORS.error)};
    padding: calc(0.2rem - 2px) calc(0.4rem - 2px) !important;
    transition: box-shadow ${TIMINGS.transitionNormal} ease,
      padding ${TIMINGS.transitionNormal} ease;
  }

  &[data-validation-error][data-validation-status='fading'] {
    background: ${COLORS.white};
    box-shadow: inset 0 0 0 2px transparent;
    padding: 0.2rem 0.4rem !important;
    transition: box-shadow ${TIMINGS.transitionSlow} ease,
      padding ${TIMINGS.transitionSlow} ease;
  }

  &[data-column-hiding] {
    opacity: 0;
    transition: opacity ${TIMINGS.transitionMedium} ease;
    will-change: opacity;
  }

  &[data-column-showing] {
    ${fadeInKeyframes}
  }

  &[data-locked] {
    background: ${COLORS.lightGray};
    box-shadow: ${SHADOWS.borderThin(COLORS.mediumGray)};
    padding: calc(0.2rem - 1px);
    cursor: not-allowed;
    opacity: 0.7;
  }

  &[data-editable]:not([data-editing]):not([data-locked]):hover
    > div[data-edit-icon] {
    display: flex;
  }
  &:hover > div[data-edit-icon][data-disabled='true'] {
    display: flex;
  }
  &:hover > div[data-add-to-job-icon] {
    display: flex;
  }

  &[data-editing] > div[data-edit-icon] {
    display: none !important;
  }
`;

const CellIconBase = styled.div`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: none;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 50%;
  padding: 4px;
  z-index: 10;
  cursor: pointer;
  min-height: 14px;

  .MuiSvgIcon-root {
    color: ${COLORS.white};
    font-size: 10px;
  }

  &:hover {
    background: ${COLORS.iconHover};
  }
`;

export const EditIcon = styled(CellIconBase)<{
  $isDisabled?: boolean;
}>`
  transition: opacity ${TIMINGS.transitionFast} ease;

  ${(props) =>
    props.$isDisabled &&
    `
    background: ${COLORS.dividerGray};
    cursor: default;

    .MuiSvgIcon-root {
      color: #f0f0f0;
    }

    &:hover {
      background: ${COLORS.dividerGray};
    }
  `}
`;

export const AddToJobIcon = styled(CellIconBase)<{
  $isDisabled?: boolean;
}>`
  transition: background ${TIMINGS.transitionFast} ease;

  ${(props) =>
    props.$isDisabled &&
    `
    background: ${COLORS.dividerGray};
    cursor: default;

    .MuiSvgIcon-root {
      color: #f0f0f0;
    }

    &:hover {
      background: ${COLORS.dividerGray};
    }
  `}
`;

export const ValidationErrorIcon = styled.div<{
  $status?: 'active' | 'fading' | null;
}>`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.error};
  border-radius: 50%;
  padding: 4px;
  margin-right: 0;
  z-index: 10;
  cursor: pointer;
  transition: opacity ${TIMINGS.transitionFast} ease,
    background-color ${TIMINGS.transitionFast} ease;

  .MuiSvgIcon-root {
    color: ${COLORS.white};
    font-size: 10px;
  }

  &:hover {
    background: #c62828;
  }

  ${(props) =>
    props.$status === 'fading' &&
    `
    opacity: 0;
    transition: opacity ${TIMINGS.transitionSlow} ease, 
                background-color ${TIMINGS.transitionSlow} ease;
  `}
`;

export const LockedUserCircle = styled.div<{
  $backgroundColor: string;
}>`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$backgroundColor};
  color: ${COLORS.white};
  border-radius: 50%;
  width: 14px;
  height: 14px;
  font-size: 8px;
  font-weight: bold;
  z-index: 10;
  box-shadow: ${SHADOWS.icon};
`;
